import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useActiveCompany } from "../../context/ActiveCompanyContext";
import PageHeader from "../../components/PageHeader";
import { ActiveBadge } from "../../components/StatusBadge";
import EmptyState from "../../components/EmptyState";
import VatConfigPanel from "../../components/admin/VatConfigPanel";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function VatPage() {
  const { isAdmin } = useAuth();
  const { companyId, setCompanyId } = useActiveCompany();
  const [rates, setRates] = useState([]);
  const [companies, setCompanies] = useState([]);

  const [rateDialog, setRateDialog] = useState(false);
  const [editingRate, setEditingRate] = useState(null);
  const [rateForm, setRateForm] = useState({ name: "", rate: "", active_from: "", active_to: "" });

  const load = async () => {
    const [r, c] = await Promise.all([api.get("/vat/rates"), api.get("/companies")]);
    setRates(r.data);
    setCompanies(c.data);
    if (c.data.length > 0 && !c.data.some((co) => co.id === companyId)) setCompanyId(c.data[0].id);
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCompany = companies.find((co) => co.id === companyId) || null;

  const saveRate = async () => {
    const payload = {
      name: rateForm.name,
      rate: Number(rateForm.rate),
      active_from: rateForm.active_from || null,
      active_to: rateForm.active_to || null,
    };
    try {
      if (editingRate) {
        await api.patch(`/vat/rates/${editingRate.id}`, payload);
        toast.success("VAT rate updated");
      } else {
        await api.post("/vat/rates", payload);
        toast.success("VAT rate created");
      }
      setRateDialog(false);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleRate = async (r) => {
    try {
      await api.patch(`/vat/rates/${r.id}`, { active: !r.active });
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="vat-page">
      <PageHeader
        title="VAT"
        subtitle="Simplified Swedish VAT model — rates and company reporting configuration"
      />

      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-semibold text-slate-800">Company VAT Configuration</h2>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="vat-config-context">
            {selectedCompany
              ? `You are editing VAT configuration for ${selectedCompany.name}`
              : "Select a company to maintain its VAT reporting configuration."}
          </p>
        </div>
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger className="w-64" data-testid="vat-company-select">
            <SelectValue placeholder="Select company" />
          </SelectTrigger>
          <SelectContent>
            {companies.map((c) => (
              <SelectItem key={c.id} value={c.id} data-testid={`vat-company-option-${c.id}`}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedCompany && <VatConfigPanel company={selectedCompany} isAdmin={isAdmin} />}

      <div className="mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-slate-800">VAT Rates</h2>
          {isAdmin && (
            <Button
              size="sm"
              className="bg-[#0052CC] hover:bg-[#0047B3]"
              onClick={() => {
                setEditingRate(null);
                setRateForm({ name: "", rate: "", active_from: "", active_to: "" });
                setRateDialog(true);
              }}
              data-testid="new-vat-rate-button"
            >
              <Plus className="h-4 w-4 mr-1" /> New rate
            </Button>
          )}
        </div>
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="vat-rates-table">
          <table className="w-full">
            <thead>
              <tr className="bg-secondary/50 border-b border-slate-200">
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Name</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Rate</th>
                <th className="text-left text-xs font-semibold text-slate-500 uppercase tracking-wider py-3 px-4">Status</th>
                <th className="py-3 px-4" />
              </tr>
            </thead>
            <tbody>
              {rates.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-accent/40 transition-colors" data-testid={`vat-rate-row-${r.id}`}>
                  <td className="py-3 px-4 text-sm font-semibold text-slate-800">{r.name}</td>
                  <td className="py-3 px-4 text-sm text-slate-700">{r.rate} %</td>
                  <td className="py-3 px-4"><ActiveBadge active={r.active} testId={`vat-rate-status-${r.id}`} /></td>
                  <td className="py-3 px-4 text-right">
                    {isAdmin && (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingRate(r);
                            setRateForm({ name: r.name, rate: String(r.rate), active_from: r.active_from || "", active_to: r.active_to || "" });
                            setRateDialog(true);
                          }}
                          data-testid={`edit-vat-rate-${r.id}`}
                        >
                          Edit
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => toggleRate(r)} data-testid={`toggle-vat-rate-${r.id}`}>
                          {r.active ? "Deactivate" : "Activate"}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rates.length === 0 && <EmptyState title="No VAT rates" testId="vat-rates-empty" />}
        </div>
      </div>

      <Dialog open={rateDialog} onOpenChange={setRateDialog}>
        <DialogContent data-testid="vat-rate-dialog">
          <DialogHeader>
            <DialogTitle>{editingRate ? "Edit VAT rate" : "New VAT rate"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Name</Label>
                <Input value={rateForm.name} onChange={(e) => setRateForm({ ...rateForm, name: e.target.value })} placeholder="Reduced VAT" data-testid="vat-rate-name-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Rate (%)</Label>
                <Input type="number" step="0.01" min="0" max="100" value={rateForm.rate} onChange={(e) => setRateForm({ ...rateForm, rate: e.target.value })} data-testid="vat-rate-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Active from</Label>
                <Input type="date" value={rateForm.active_from} onChange={(e) => setRateForm({ ...rateForm, active_from: e.target.value })} data-testid="vat-rate-active-from-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Active to</Label>
                <Input type="date" value={rateForm.active_to} onChange={(e) => setRateForm({ ...rateForm, active_to: e.target.value })} data-testid="vat-rate-active-to-input" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDialog(false)} data-testid="vat-rate-cancel-button">Cancel</Button>
            <Button onClick={saveRate} className="bg-[#0052CC] hover:bg-[#0047B3]" data-testid="vat-rate-save-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
